import { Box, Divider, Grid, InputAdornment, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  FunctionField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleFormIterator,
  SimpleForm,
  TextField,
  TextInput,
  Validator,
  email,
  regex,
  required,
  useRecordContext,
} from "react-admin";
import { useFormState, useWatch } from "react-hook-form";
import { IfCanAccess } from "@react-admin/ra-rbac";
import { FormItem } from "../../../components/Common/FormField";
import {
  ApiKeyValue,
  LivePaymentPagePreview,
  CopyableValue,
  LogoUpload,
  PROJECT_TYPE_CHOICES,
  ProjectSection,
  SwatchColorInput,
  projectTypeLabel,
} from "./projectFields";

const GAS_CURRENCY_CHOICES = ["ETH", "POL", "MATIC", "XTZ"].map((id) => ({ id, name: id }));
const DESTINATION_MODE_CHOICES = [
  { id: "whitelist", name: "Whitelist" },
  { id: "free", name: "Free" },
];

const httpsUrl = regex(/^https:\/\/\S+$/, "The URL must start with https://");
const uuid = regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Expected a UUID");

// A value saved before the rule existed is kept as is; a new or changed one is checked,
// as the API does for webhook URLs.
const useRuleForNewValue = (source: string, rule: Validator): Validator => {
  const saved = source.split(".").reduce((node: any, key) => node?.[key], useRecordContext());
  return (value, values, props) => (value === saved ? undefined : rule(value, values, props));
};

// The rate is stored as a fraction (0.01) and edited as a percentage (1).
const percent = {
  format: (rate?: number | null) => (rate == null ? rate : Math.round(rate * 10000) / 100),
  parse: (value?: number | string | null) => (value === "" || value == null ? null : Number(value) / 100),
};

const unit = (text?: string) => (text ? { endAdornment: <InputAdornment position="end">{text}</InputAdornment> } : undefined);

// A new entity starts active, with the schema's conversion and fees defaults. Not applied
// on edit, where they would fill an existing entity's unset values.
const CREATE_DEFAULTS = { status: "active", autoConvert: false, feesIncluded: true };

type CreateProp = { create?: boolean };

const InfoInputs = ({ create }: CreateProp) => {
  const { t }: { t: any } = useTranslation();
  return (
    <ProjectSection id="info" form>
      {!create && (
        <>
          <FormItem half label={t("ID")}>
            <CopyableValue source="id" />
          </FormItem>
          <FormItem half label={t("Code")}>
            <CopyableValue source="codeProject" />
          </FormItem>
        </>
      )}
      <FormItem half>
        <TextInput source="name" label={t("Name")} validate={required()} fullWidth />
      </FormItem>
      <FormItem half label={t("Account")}>
        {create ? (
          <ReferenceInput source="accountId" reference="accounts">
            <AutocompleteInput label={t("Account")} validate={required()} fullWidth />
          </ReferenceInput>
        ) : (
          <ReferenceField source="accountId" reference="accounts">
            <TextField source="name" />
          </ReferenceField>
        )}
      </FormItem>
      {/* The type picks the parameters: it is chosen once, when the entity is created. */}
      <FormItem half label={t("Type")}>
        {create ? (
          <SelectInput source="typeProject" label={t("Type")} choices={PROJECT_TYPE_CHOICES} validate={required()} fullWidth />
        ) : (
          <FunctionField render={(record: any) => t(projectTypeLabel(record.typeProject))} />
        )}
      </FormItem>
      <FormItem half>
        {/* Stored as "active" / "inactive"; an inactive entity takes no payment or transfer. */}
        <BooleanInput
          source="status"
          label={t("Active")}
          format={(status?: string) => status === "active"}
          parse={(active: boolean) => (active ? "active" : "inactive")}
          helperText={false}
        />
      </FormItem>
    </ProjectSection>
  );
};

const CryptoPaymentInputs = ({ create }: CreateProp) => {
  const { t }: { t: any } = useTranslation();
  const webhookRule = useRuleForNewValue("parameters.webhookUrl", httpsUrl);
  // Required for a new entity only: existing ones may predate the rule.
  const requiredOnCreate = create ? required() : undefined;
  return (
    <>
      <ProjectSection id="paymentPage" form>
        <FormItem>
          <LivePaymentPagePreview />
        </FormItem>
        <FormItem half>
          <TextInput source="parameters.physicalPayment.name" label={t("Name")} validate={requiredOnCreate} fullWidth />
        </FormItem>
        <FormItem half>
          <TextInput source="parameters.physicalPayment.email" label={t("Email")} validate={[required(), email()]} fullWidth />
        </FormItem>
        <FormItem>
          <TextInput source="parameters.physicalPayment.description" label={t("Description")} validate={requiredOnCreate} fullWidth />
        </FormItem>
        <FormItem half>
          <TextInput source="parameters.physicalPayment.phone" label={t("Phone")} fullWidth />
        </FormItem>
        <FormItem half>
          <TextInput source="parameters.physicalPayment.address" label={t("Address")} fullWidth />
        </FormItem>
        <FormItem half label={t("Inverted logo")}>
          <LogoUpload field="logo" isRequired={create} />
        </FormItem>
        <FormItem half label={t("Logo")}>
          <LogoUpload field="logoInverse" isRequired={create} />
        </FormItem>
        <FormItem half label={t("Background color")}>
          <SwatchColorInput source="parameters.physicalPayment.backgroundColor" label={t("Background color")} isRequired />
        </FormItem>
        <FormItem half label={t("Font color")}>
          <SwatchColorInput source="parameters.physicalPayment.fontColor" label={t("Font color")} />
        </FormItem>
      </ProjectSection>
      <ProjectSection id="cryptoPayment" form>
        <FormItem half>
          <BooleanInput source="autoConvert" label={t("Auto conversion")} helperText={false} />
        </FormItem>
        <FormItem half>
          <BooleanInput source="feesIncluded" label={t("Fees included")} helperText={false} />
        </FormItem>
        <FormItem>
          <TextInput source="parameters.webhookUrl" label={t("Webhook URL")} validate={webhookRule} fullWidth />
        </FormItem>
        {!create && (
          <FormItem label={t("API key")}>
            <ApiKeyValue />
          </FormItem>
        )}
      </ProjectSection>
    </>
  );
};

const GasStationInputs = () => {
  const { t }: { t: any } = useTranslation();
  const webhookRule = useRuleForNewValue("parameters.webhookUrl", httpsUrl);
  const [currency, destinationMode] = useWatch({ name: ["parameters.gasStation.currency", "parameters.gasStation.destinationMode"] });
  return (
    <ProjectSection id="gasStation" form>
      <FormItem half>
        <SelectInput source="parameters.gasStation.currency" label={t("Currency")} choices={GAS_CURRENCY_CHOICES} validate={required()} fullWidth />
      </FormItem>
      <FormItem half>
        <NumberInput source="parameters.gasStation.limitPer24H" label={t("Volume available per 24 hours")} validate={required()} InputProps={unit(currency)} fullWidth />
      </FormItem>
      <FormItem>
        <TextInput source="parameters.webhookUrl" label={t("Webhook URL")} validate={webhookRule} fullWidth />
      </FormItem>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <FormItem half>
        <NumberInput source="parameters.gasStation.approvalThresholdEur" label={t("Approval threshold")} InputProps={unit("EUR")} fullWidth />
      </FormItem>
      <FormItem half>
        <NumberInput source="parameters.gasStation.commissionRate" label={t("Commission rate")} {...percent} InputProps={unit("%")} fullWidth />
      </FormItem>
      <FormItem half>
        <SelectInput source="parameters.gasStation.destinationMode" label={t("Destination mode")} choices={DESTINATION_MODE_CHOICES} fullWidth />
      </FormItem>
      {destinationMode === "whitelist" && (
        <FormItem>
          <ArrayInput source="parameters.gasStation.allowedAddresses" label={t("Allowed addresses")}>
            <SimpleFormIterator disableReordering fullWidth>
              <TextInput source="" label={t("Address")} helperText={false} sx={{ "& input": { fontFamily: "monospace" } }} fullWidth />
            </SimpleFormIterator>
          </ArrayInput>
        </FormItem>
      )}
    </ProjectSection>
  );
};

const CoinhouseInputs = ({ create, gasStation }: CreateProp & { gasStation: boolean }) => {
  const { t }: { t: any } = useTranslation();
  const userRule = useRuleForNewValue("userIdCNHS", uuid);
  return (
    <IfCanAccess resource="projects.userIdCNHS" action={create ? "create" : "edit"}>
      <ProjectSection id="coinhouse" form>
        <FormItem>
          <TextInput source="userIdCNHS" label={t("Coinhouse user UUID")} validate={userRule} sx={{ "& input": { fontFamily: "monospace" } }} fullWidth />
        </FormItem>
        {gasStation && !create && (
          <IfCanAccess resource="projects.fields.autorizedByCNHS" action="edit">
            <FormItem>
              <BooleanInput source="autorizedByCNHS" label={t("Authorized by Coinhouse")} helperText={false} />
            </FormItem>
          </IfCanAccess>
        )}
      </ProjectSection>
    </IfCanAccess>
  );
};

// The sections follow the type selected in the form, not the saved record.
const TypeInputs = ({ create }: CreateProp) => {
  const type = useWatch({ name: "typeProject" });
  if (!type) return null;
  return (
    <>
      {type === "cryptoPayment" && <CryptoPaymentInputs create={create} />}
      {type === "gasStation" && <GasStationInputs />}
      <CoinhouseInputs create={create} gasStation={type === "gasStation"} />
    </>
  );
};

// The save bar: it stays at the bottom of the screen while the form scrolls, and says
// whether there is anything to save (the button stays disabled until then).
const SaveBar = ({ create }: CreateProp) => {
  const { t }: { t: any } = useTranslation();
  const { isDirty } = useFormState();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        justifyContent: "space-between",
        gap: 2,
        px: { xs: 3, md: 5 },
        py: 2,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {isDirty ? t("You have unsaved changes.") : create ? "" : t("No changes yet.")}
      </Typography>
      <SaveButton variant="contained" label={t(create ? "Create entity" : "Save changes")} />
    </Box>
  );
};

// One sheet for creating and editing an entity: its identity, then what its type configures.
const ProjectForm = ({ create }: CreateProp) => {
  return (
    <SimpleForm defaultValues={create ? CREATE_DEFAULTS : undefined} sx={{ px: { xs: 3, md: 5 }, py: 0 }} toolbar={<SaveBar create={create} />}>
      <InfoInputs create={create} />
      <TypeInputs create={create} />
    </SimpleForm>
  );
};

export default ProjectForm;
